import { useEffect, useMemo, useState } from "react";
import {
  CalendarClock,
  Loader2,
  Mail,
  MessageSquareText,
  RefreshCcw,
  Store,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { PanelCard } from "@/admin/components/PanelCard";
import { StatCard } from "@/admin/components/StatCard";
import { listCollectionByCreatedAt, updateDocById } from "@/lib/store";

function formatDateTime(value) {
  if (!value) return "-";
  if (typeof value?.toDate === "function") {
    return value.toDate().toLocaleString();
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
}

function createdAtMs(value) {
  if (!value) return 0;
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.toMillis === "function") return value.toMillis();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function StatusPill({ status }) {
  const text = status || "pending";
  const normalized = String(text).toLowerCase();
  const tone =
    normalized === "approved"
      ? "bg-[#e8fff2] text-[#0f8a4b]"
      : normalized === "rejected"
        ? "bg-[#fff1f1] text-[#b42318]"
        : "bg-[#eaf1ff] text-[#0b5bd3]";
  return (
    <span className={["rounded-full px-3 py-1 text-xs font-semibold capitalize", tone].join(" ")}>
      {text}
    </span>
  );
}

function ImageCell({ src, alt }) {
  if (!src) {
    return <span className="text-xs text-[#8da0c0]">No image</span>;
  }

  return (
    <a href={src} target="_blank" rel="noreferrer" className="inline-flex">
      <img
        src={src}
        alt={alt}
        className="h-12 w-12 rounded-xl border border-[#d6e3ff] object-cover"
        loading="lazy"
      />
    </a>
  );
}

function TableCard({ title, subtitle, icon: Icon, rows, columns }) {
  return (
    <PanelCard className="p-6">
      <div className="mb-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#edf3ff] text-[#0033ff]">
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-display text-2xl font-extrabold tracking-tight text-[#0b1221]">
              {title}
            </h3>
            <p className="text-sm text-[#6c7c98]">{subtitle}</p>
          </div>
        </div>
        <span className="rounded-full bg-[#edf3ff] px-3 py-1 text-xs font-semibold text-[#0b5bd3]">
          {rows.length} records
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border-separate border-spacing-y-2">
          <thead>
            <tr className="text-left text-xs uppercase tracking-[0.16em] text-[#7a8db6]">
              {columns.map((column) => (
                <th key={column.key} className="px-3 py-1">{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((row) => (
                <tr key={row.id} className="bg-[#f9fbff] text-sm text-[#28405b] shadow-sm">
                  {columns.map((column, index) => (
                    <td
                      key={column.key}
                      className={[
                        "px-3 py-3 align-top",
                        index === 0 ? "rounded-l-2xl" : "",
                        index === columns.length - 1 ? "rounded-r-2xl" : "",
                      ].join(" ")}
                    >
                      {column.render ? column.render(row) : row[column.key] || "-"}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="rounded-2xl bg-[#f9fbff] px-4 py-8 text-center text-sm text-[#7a8db6]">
                  No submissions yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </PanelCard>
  );
}

export function DashboardPage() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState({
    partners: [],
    riders: [],
    contacts: [],
  });
  const [updatingKey, setUpdatingKey] = useState("");

  async function loadData(showSpinner = true) {
    if (showSpinner) setLoading(true);
    setRefreshing(true);
    setError("");

    try {
      const [partners, riders, contacts] = await Promise.all([
        listCollectionByCreatedAt("partners", 200),
        listCollectionByCreatedAt("riders", 200),
        listCollectionByCreatedAt("contacts", 200),
      ]);

      setData({ partners, riders, contacts });
    } catch {
      setError("Could not load Firestore submissions. Please verify rules and network.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    loadData(true);
  }, []);

  async function updateApplicationStatus(collectionName, docId, nextStatus) {
    const actionKey = `${collectionName}:${docId}:${nextStatus}`;
    setUpdatingKey(actionKey);

    try {
      await updateDocById(collectionName, docId, { status: nextStatus });

      // Optimistic in-memory update so status changes without waiting for network round trip.
      setData((prev) => ({
        ...prev,
        [collectionName]: (prev[collectionName] || []).map((item) =>
          item.id === docId ? { ...item, status: nextStatus } : item
        ),
      }));

      await loadData(false);
      toast.success(`Application ${nextStatus.toLowerCase()} successfully.`);
    } catch {
      toast.error("Failed to update status. Please try again.");
    } finally {
      setUpdatingKey("");
    }
  }

  const summary = useMemo(
    () => {
      const allCreatedAt = [
        ...data.partners.map((x) => x.createdAt),
        ...data.riders.map((x) => x.createdAt),
        ...data.contacts.map((x) => x.createdAt),
      ];
      const latest = allCreatedAt.reduce(
        (max, current) => (createdAtMs(current) > createdAtMs(max) ? current : max),
        null
      );

      return [
      {
        title: "Partner applications",
        value: String(data.partners.length),
        delta: "Live from partners",
        icon: Store,
      },
      {
        title: "Rider applications",
        value: String(data.riders.length),
        delta: "Live from riders",
        icon: UserRound,
        tone: "navy",
      },
      {
        title: "Contact messages",
        value: String(data.contacts.length),
        delta: "Live from contacts",
        icon: Mail,
      },
      {
        title: "Latest submission",
        value: formatDateTime(latest),
        delta: "Most recent createdAt",
        icon: CalendarClock,
      },
    ];
    },
    [data.contacts, data.partners, data.riders]
  );

  const partnerColumns = [
    { key: "createdAt", label: "Created", render: (row) => formatDateTime(row.createdAt) },
    { key: "status", label: "Status", render: (row) => <StatusPill status={row.status} /> },
    {
      key: "photoUrl",
      label: "Image",
      render: (row) => <ImageCell src={row.photoUrl} alt={`${row.shopName || "Partner"} photo`} />,
    },
    { key: "shopName", label: "Shop" },
    { key: "ownerName", label: "Owner" },
    { key: "mobile", label: "Mobile" },
    { key: "whatsapp", label: "WhatsApp" },
    { key: "email", label: "Email" },
    { key: "category", label: "Category" },
    { key: "address", label: "Address" },
    { key: "mapLocation", label: "Map Link" },
    { key: "notes", label: "Notes" },
    {
      key: "actions",
      label: "Actions",
      render: (row) => {
        const approveKey = `partners:${row.id}:Approved`;
        const rejectKey = `partners:${row.id}:Rejected`;
        const isApproving = updatingKey === approveKey;
        const isRejecting = updatingKey === rejectKey;

        return (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => updateApplicationStatus("partners", row.id, "Approved")}
              disabled={isApproving || isRejecting}
              className="rounded-lg bg-[#16a34a] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#15803d] disabled:opacity-60"
            >
              {isApproving ? "..." : "Approve"}
            </button>
            <button
              type="button"
              onClick={() => updateApplicationStatus("partners", row.id, "Rejected")}
              disabled={isApproving || isRejecting}
              className="rounded-lg bg-[#dc2626] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#b91c1c] disabled:opacity-60"
            >
              {isRejecting ? "..." : "Reject"}
            </button>
          </div>
        );
      },
    },
  ];

  const riderColumns = [
    { key: "createdAt", label: "Created", render: (row) => formatDateTime(row.createdAt) },
    { key: "status", label: "Status", render: (row) => <StatusPill status={row.status} /> },
    {
      key: "photoUrl",
      label: "Image",
      render: (row) => <ImageCell src={row.photoUrl} alt={`${row.fullName || "Rider"} photo`} />,
    },
    { key: "fullName", label: "Name" },
    { key: "mobile", label: "Mobile" },
    { key: "whatsapp", label: "WhatsApp" },
    { key: "vehicleType", label: "Vehicle" },
    { key: "deliveryArea", label: "Delivery Area" },
    { key: "address", label: "Address" },
    {
      key: "idProofUrl",
      label: "ID Proof",
      render: (row) =>
        row.idProofUrl ? (
          <a className="text-[#0b5bd3] underline" href={row.idProofUrl} target="_blank" rel="noreferrer">
            View file
          </a>
        ) : (
          "-"
        ),
    },
    { key: "notes", label: "Notes" },
    {
      key: "actions",
      label: "Actions",
      render: (row) => {
        const approveKey = `riders:${row.id}:Approved`;
        const rejectKey = `riders:${row.id}:Rejected`;
        const isApproving = updatingKey === approveKey;
        const isRejecting = updatingKey === rejectKey;

        return (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => updateApplicationStatus("riders", row.id, "Approved")}
              disabled={isApproving || isRejecting}
              className="rounded-lg bg-[#16a34a] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#15803d] disabled:opacity-60"
            >
              {isApproving ? "..." : "Approve"}
            </button>
            <button
              type="button"
              onClick={() => updateApplicationStatus("riders", row.id, "Rejected")}
              disabled={isApproving || isRejecting}
              className="rounded-lg bg-[#dc2626] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#b91c1c] disabled:opacity-60"
            >
              {isRejecting ? "..." : "Reject"}
            </button>
          </div>
        );
      },
    },
  ];

  const contactColumns = [
    { key: "createdAt", label: "Created", render: (row) => formatDateTime(row.createdAt) },
    { key: "status", label: "Status", render: (row) => <StatusPill status={row.status || "new"} /> },
    { key: "image", label: "Image", render: () => <span className="text-xs text-[#8da0c0]">N/A</span> },
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    {
      key: "message",
      label: "Message",
      render: (row) => <p className="max-w-[380px] whitespace-pre-wrap">{row.message || "-"}</p>,
    },
  ];

  return (
    <div className="space-y-4 pb-6">
      <PanelCard className="overflow-hidden p-0">
        <div className="flex flex-col gap-5 bg-[linear-gradient(135deg,#0033ff_0%,#2e6cff_55%,#dbe7ff_100%)] px-6 py-6 text-white sm:px-8 sm:py-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/80">
                Firestore-connected admin
              </p>
              <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
                Partner, rider, and contact submissions in one place.
              </h2>
            </div>
            <button
              type="button"
              onClick={() => loadData(false)}
              disabled={refreshing}
              className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/25 disabled:opacity-70"
            >
              {refreshing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCcw className="h-4 w-4" />
              )}
              Refresh data
            </button>
          </div>
          <p className="max-w-3xl text-sm leading-7 text-white/84 sm:text-base">
            This panel reads directly from Firestore collections: partners, riders, and contacts. Each row includes createdAt, status, and image/file where available.
          </p>
        </div>
      </PanelCard>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {summary.map((stat) => (
          <StatCard key={stat.title} {...stat} />
        ))}
      </section>

      {error ? (
        <PanelCard className="p-5">
          <p className="text-sm text-[#b42318]">{error}</p>
        </PanelCard>
      ) : null}

      {loading ? (
        <PanelCard className="p-8">
          <div className="flex items-center justify-center gap-3 text-[#5a6f96]">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading Firestore submissions...
          </div>
        </PanelCard>
      ) : (
        <section className="space-y-4">
          <TableCard
            title="Partner Applications"
            subtitle="Collection: partners"
            icon={Store}
            rows={data.partners}
            columns={partnerColumns}
          />

          <TableCard
            title="Rider Applications"
            subtitle="Collection: riders"
            icon={UserRound}
            rows={data.riders}
            columns={riderColumns}
          />

          <TableCard
            title="Contact Messages"
            subtitle="Collection: contacts"
            icon={MessageSquareText}
            rows={data.contacts}
            columns={contactColumns}
          />
        </section>
      )}
    </div>
  );
}